import {
    AcuantCameraHTMLCanvasElement,
    AcuantCameraOnDetectedResult
} from "@idw-types/acuant-camera-ui";
import { CaptureStepTypes } from "@enums/capture-step-types";
import { isDeviceAffectedByIOS16Issue, isIOS } from "./device-util";

const isSafari = () => {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.indexOf('safari') != -1) {
        if (ua.indexOf('chrome') > -1) {
            return false;
        } else {
            return true;
        }
    }
    return false;
}

const getDimension = (uiCanvas: HTMLCanvasElement) => {
    if (isSafari()) {
        return {
            height: Math.min(document.body.clientHeight, uiCanvas.height),
            width: Math.min(document.body.clientWidth, uiCanvas.width)
        };
    }
    else {
        return {
            height: uiCanvas.height,
            width: uiCanvas.width
        };
    }
}

export const drawText = (
    uiCanvas: AcuantCameraHTMLCanvasElement,
    uiContext: CanvasRenderingContext2D,
    text: string,
    fontWeight = 0.04,
    color = '#ffffff',
    showBackRect = true
) => {
    const dimension = getDimension(uiCanvas);
    const currentOrientation = window.orientation;
    const measured = uiContext.measureText(text);
    const offsetY = (Math.max(dimension.width, dimension.height) * 0.01);
    const offsetX = (Math.max(dimension.width, dimension.height) * 0.02);

    let x = (dimension.height - offsetX - measured.width) / 2;
    let y = -((dimension.width / 2) - offsetY);
    let rotation = 90;

    if (currentOrientation !== 0) {
        rotation = 0;
        x = (dimension.width - offsetY - measured.width) / 2;
        y = (dimension.height / 2) - offsetX + (Math.max(dimension.width, dimension.height) * 0.04);
    }

    uiContext.rotate(rotation * Math.PI / 180);

    if (showBackRect) {
        uiContext.fillStyle = 'rgba(0, 0, 0, 0.5)';
        uiContext.fillRect(
            Math.round(x - offsetY),
            Math.round(y + offsetY),
            Math.round(measured.width + offsetX),
            -Math.round((Math.max(dimension.width, dimension.height) * 0.05))
        );
    }

    uiContext.font = (Math.ceil(Math.max(dimension.width, dimension.height) * fontWeight) || 0) + 'px Sans-serif';
    uiContext.fillStyle = color;
    uiContext.fillText(text, x, y);
    // undo rotation to keep the ui in the same position
    uiContext.rotate(-(rotation * Math.PI / 180));
};

export const drawOverlay = (
    uiContext: CanvasRenderingContext2D,
    onDetectedResult: AcuantCameraOnDetectedResult,
    style: string
) => {
    if (
        onDetectedResult &&
        onDetectedResult.points &&
        onDetectedResult.points.length === 4
    ) {
        uiContext.beginPath();

        uiContext.moveTo(
            Math.round(onDetectedResult.points[0].x),
            Math.round(onDetectedResult.points[0].y)
        );

        for (let i = 1; i < onDetectedResult.points.length; i++) {
            uiContext.lineTo(
                Math.round(onDetectedResult.points[i].x),
                Math.round(onDetectedResult.points[i].y)
            );
        }
        uiContext.fillStyle = style;
        uiContext.strokeStyle = 'rgba(0, 0, 0, 0)';
        uiContext.stroke();
        uiContext.fill();
    }
};

const generateCorners = (
    uiCanvas: AcuantCameraHTMLCanvasElement,
    uiContext: CanvasRenderingContext2D,
    fillStyle: string,
    captureStepType: CaptureStepTypes
) => {
    const dimension = getDimension(uiCanvas);
    uiContext.lineWidth = (Math.ceil(Math.max(dimension.width, dimension.height) * 0.0025) || 1);
    uiContext.strokeStyle = fillStyle;

    const center = {
        x: dimension.width / 2,
        y: dimension.height / 2
    };

    let targetWidth, targetHeight;

    const isPassport = captureStepType === CaptureStepTypes.PASSPORT;

    let maxRatioUpperBoundThreshold = 0.85;
    if (isPassport) {
        maxRatioUpperBoundThreshold = 0.95;
    }

    if (isIOS()) {
        if (isDeviceAffectedByIOS16Issue()) {
            if (isPassport) {
                maxRatioUpperBoundThreshold = 0.8;
            } else {
                maxRatioUpperBoundThreshold = 0.48
            }
        } else if (isPassport) {
            maxRatioUpperBoundThreshold = 1;
        }
    }

    if (dimension.width > dimension.height) {
        targetWidth = maxRatioUpperBoundThreshold * dimension.width;
        targetHeight = maxRatioUpperBoundThreshold * dimension.width / 1.58870;
        if (targetHeight > maxRatioUpperBoundThreshold * dimension.height) {
            targetWidth = targetWidth / targetHeight * maxRatioUpperBoundThreshold * dimension.height;
            targetHeight = maxRatioUpperBoundThreshold * dimension.height;
        }
    } else {
        targetWidth = maxRatioUpperBoundThreshold * dimension.height / 1.58870;
        targetHeight = maxRatioUpperBoundThreshold * dimension.height;
        if (targetWidth > maxRatioUpperBoundThreshold * dimension.width) {
            targetHeight = targetHeight / targetWidth * maxRatioUpperBoundThreshold * dimension.width;
            targetWidth = maxRatioUpperBoundThreshold * dimension.width;
        }
    }

    const offsetX = targetWidth / 2;
    const offsetY = targetHeight / 2;

    const defaultCorners = [
        { x: center.x - offsetX, y: center.y - offsetY },
        { x: center.x + offsetX, y: center.y - offsetY },
        { x: center.x + offsetX, y: center.y + offsetY },
        { x: center.x - offsetX, y: center.y + offsetY }
    ];

    return defaultCorners;
};

export const drawPermanentOverlay = (
    uiCanvas: AcuantCameraHTMLCanvasElement,
    uiContext: CanvasRenderingContext2D,
    fillStyle: string,
    captureStepType: CaptureStepTypes
) => {
    const defaultCorners = generateCorners(
        uiCanvas,
        uiContext,
        fillStyle,
        captureStepType
    );

    uiContext.beginPath();

    uiContext.moveTo(
        Math.round(defaultCorners[0].x),
        Math.round(defaultCorners[0].y)
    );

    for (let i = 1; i < defaultCorners.length; i++) {
        uiContext.lineTo(
            Math.round(defaultCorners[i].x),
            Math.round(defaultCorners[i].y)
        );
    }
    uiContext.fillStyle = fillStyle;
    uiContext.strokeStyle = 'rgba(173, 216, 230, 0.4)';
    uiContext.stroke();
    uiContext.fill();
};